// Build a Spanish-keyed /Buscar URL query string from a Google PlaceResult
// + the homepage's selected type/operation. Lives next to the rest of the
// Buscar code so the URL contract has one source of truth.
//
// Mirrors useSearchState.STATE_TO_URL_KEY and the SSR parser in
// /Buscar/index.js. Default operation 'SALE' (Comprar) is stripped — same
// as the user-typed Buscar default.
//
// Replaces the homepage submit's `queryGenerator` call (which produces
// legacy English keys: type=, operation=, latitude=, longitude=,
// keywords=). queryGenerator stays in place for the regional landing
// pages (zmg/cancun/cdmx/etc.) per DW.buscadores.1, which still ship
// to legacy `/Buscar` and need its English-key URLs.

import { computePlaceRadius } from "./computePlaceRadius";

const DEFAULT_OPERATION = "SALE";

const findComponent = (place, type) =>
    place?.address_components?.find((c) => c.types.includes(type))?.long_name;

// Mirrors LocationInput's display-name logic (TopBar.jsx):
// sublocality_level_1 → prefer place.name (the colonia's actual name);
// otherwise fall back to locality → place.name → raw text.
const getLocationName = (place, fallback) => {
    const sublocality = findComponent(place, "sublocality_level_1");
    const locality = findComponent(place, "locality");
    if (sublocality && place.name) return place.name;
    return locality || place.name || fallback;
};

// Rough estimate of the zoom level that frames a circle of the given
// radius. Web Mercator math: log2(padded * 156543.03 * cos(lat) / (2*r)),
// matching the formula in BuscarLayout.fitMapToCircle. The PADDED_PX
// constant approximates a typical map pane after the
// CIRCLE_VIEWPORT_PADDING_PX is removed — accurate enough for cold-load
// framing across desktop and tablet, slightly imperfect at very narrow
// mobile widths. Pinch/scroll can refine. Clamped to a sane range so
// degenerate radii don't put the camera at unreadable zooms.
const estimateZoomForRadius = (radiusM, lat) => {
    if (!Number.isFinite(radiusM) || radiusM <= 0) return null;
    const PADDED_PX = 440;
    const cosLat = Math.cos((lat * Math.PI) / 180);
    const z = Math.log2((PADDED_PX * 156543.03392 * cosLat) / (2 * radiusM)) - 0.05;
    return Math.max(10, Math.min(18, z));
};

export const buildBuscarUrl = ({ place, typeValue, operationValue, fallbackKeyword }) => {
    const params = new URLSearchParams();

    if (typeValue) {
        const value = Array.isArray(typeValue) ? typeValue.join(",") : typeValue;
        if (value) params.set("tipo", value);
    }

    // Operation: strip the default Comprar/SALE — matches Buscar's own
    // default-stripping behavior so the URL stays clean for the common
    // case. Multi-value (explicit "Todas") writes via the `todas` sentinel.
    if (operationValue) {
        const arr = Array.isArray(operationValue) ? operationValue : [operationValue];
        const isDefault = arr.length === 1 && arr[0] === DEFAULT_OPERATION;
        if (!isDefault) {
            const value = arr.length === 1 ? arr[0] : "todas";
            params.set("operacion", value);
        }
    }

    const hasPlace = Boolean(place?.geometry?.location);
    if (hasPlace) {
        const sublocality = findComponent(place, "sublocality_level_1");
        const locality = findComponent(place, "locality");
        const name = getLocationName(place, fallbackKeyword);
        if (name) params.set("q", name);
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const { rendered: radiusM } = computePlaceRadius(place, {
            hasNeighborhood: Boolean(sublocality),
        });
        params.set("lat", String(lat));
        params.set("lng", String(lng));
        // Include radio so /Buscar can derive the boundary polygon from
        // URL state on cold load (DW.boundary.1 closure).
        params.set("radio", String(radiusM));
        // Bake the zoom in so cold-load lands with the polygon already
        // framed — otherwise defaultZoom=12 would show metro view briefly
        // before any client-side fit kicks in.
        const z = estimateZoomForRadius(radiusM, lat);
        if (z != null) params.set("z", String(z));
        if (sublocality) params.set("colonia", sublocality);
        if (locality && !sublocality) params.set("ciudad", locality);
    } else if (fallbackKeyword) {
        // No Places match — drop the raw text into q. /Buscar's
        // Enter-resolves-first-prediction path (P6.9) can re-resolve via
        // Places on landing if the user wants to upgrade the text into a
        // proper location + boundary.
        params.set("q", fallbackKeyword);
    }

    return params.toString();
};

export default buildBuscarUrl;
